import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { getGeolocation } from "../lib/geolocation";

interface UserPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export type PositionError = "denied" | "unavailable" | null;

const userPositionAtom = atom<UserPosition | null>(null);
const positionErrorAtom = atom<PositionError>(null);

export function useUserPosition() {
  const [position, setPosition] = useAtom(userPositionAtom);
  const [positionError, setPositionError] = useAtom(positionErrorAtom);
  const isFetchingRef = useRef(false);

  const refreshPosition = useCallback(() => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    getGeolocation()
      .then((pos) => {
        setPosition(pos);
        setPositionError(null);
      })
      .catch((error) => {
        // GeolocationPositionError code 1 = PERMISSION_DENIED
        if (error?.code === 1) {
          setPositionError("denied");
        } else {
          setPositionError("unavailable");
        }
      })
      .finally(() => {
        isFetchingRef.current = false;
      });
  }, [setPosition, setPositionError]);

  // Fetch position on first mount
  useEffect(() => {
    if (!position && !positionError && !isFetchingRef.current) {
      refreshPosition();
    }
  }, [position, positionError, refreshPosition]);

  return { position, refreshPosition, positionError };
}
